import {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode,
} from 'react';

type ColorContextType = {
  color: string;
  setColor: (color: string) => void;
};

export const ColorContext = createContext<ColorContextType>({
  color: '#00A6FF',
  setColor: () => {},
});

/**
 * Provider component with memoized value.
 *
 * Previously the provider was used inline as `<ColorContext.Provider value={{ color, setColor }}>`,
 * which created a new object on every parent re-render, causing ALL consumers
 * to re-render even when color hadn't changed. This cascading re-render was the
 * root cause of the camera re-fit issue (unnecessary re-renders of the Bounds
 * tree) and contributed to the editor losing syntax highlighting (unnecessary
 * re-renders of the entire subtree).
 */
export function ColorProvider({
  children,
  initialColor = '#00A6FF',
}: {
  children: ReactNode;
  initialColor?: string;
}) {
  // Use lazy initializer so the initial value is captured once and never
  // re-derived from the prop — consumers mutate color via setColor independently.
  const [color, setColor] = useState(() => initialColor);

  const value = useMemo(
    () => ({ color, setColor }),
    [color], // setColor from useState is stable — no need to list it
  );

  return (
    <ColorContext.Provider value={value}>{children}</ColorContext.Provider>
  );
}

export const useColor = () => {
  const context = useContext(ColorContext);
  if (!context) {
    throw new Error('useColor must be used within a ColorProvider');
  }
  return context;
};
